import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import CustomerFinanceApi from "../../../infrastructure/api/customerFinance";
import PaymentStatusBadge from "../payment-gateway/PaymentStatusBadge";
import {
  formatRial,
  mapOperationalStateToBadge,
  pick,
} from "../payment-gateway/paymentGatewayUtils";

const REFRESH_EVENT = "customer-finance-refresh";
const POPOVER_WIDTH = 400;

export const notifyCustomerFinanceRefresh = () => {
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
};

const HeaderFinanceMenu = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await CustomerFinanceApi.getHeaderSummary();
      const { error: err, data } = CustomerFinanceApi.normalizeHeaderSummary(res);
      if (err) {
        setError(err);
        setSummary(null);
      } else {
        setSummary(data);
      }
    } catch {
      setError("بارگذاری خلاصه مالی ناموفق بود.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    const handler = () => loadSummary();
    window.addEventListener(REFRESH_EVENT, handler);
    return () => window.removeEventListener(REFRESH_EVENT, handler);
  }, [loadSummary]);

  const openMenu = (event) => {
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
    loadSummary();
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setDrawerOpen(false);
  };

  const availableBalance = pick(summary, "availableBalance", "AvailableBalance");
  const unpaidCount = Number(
    pick(summary, "unpaidInvoiceCount", "UnpaidInvoiceCount") ?? 0
  );
  const unpaidInvoices =
    pick(summary, "latestUnpaidInvoices", "LatestUnpaidInvoices") ?? [];

  const goToInvoice = (invoiceId) => {
    closeMenu();
    if (invoiceId) {
      navigate(`/SLN/Customer/InvoicePayment/${invoiceId}`);
    }
  };

  const goTopUp = () => {
    closeMenu();
    navigate("/SLN/Customer/Balance/TopUp");
  };

  const goHistory = () => {
    closeMenu();
    navigate("/SLN/Customer/Balance/History");
  };

  const goAllInvoices = () => {
    closeMenu();
    navigate("/SLN/Customer/Requests");
  };

  const panelContent = (
    <Box sx={{ width: isMobile ? "100%" : POPOVER_WIDTH, p: 2 }}>
      {isMobile ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            فاکتورها و اعتبار پنل
          </Typography>
          <IconButton size="small" onClick={closeMenu} aria-label="بستن">
            <CloseIcon />
          </IconButton>
        </Stack>
      ) : null}

      {loading && !summary ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={64} />
          <Skeleton variant="rounded" height={100} />
        </Stack>
      ) : null}

      {error ? (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
          <Button size="small" onClick={loadSummary}>
            تلاش مجدد
          </Button>
        </Stack>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          اعتبار پنل
        </Typography>
        <Typography variant="h5" fontWeight={800} color="primary.main" sx={{ mt: 0.5, mb: 1.5 }}>
          {formatRial(availableBalance)}
        </Typography>
        <Stack direction={isMobile ? "column" : "row"} spacing={1}>
          <Button variant="contained" size="small" fullWidth onClick={goTopUp}>
            افزایش اعتبار
          </Button>
          <Button variant="outlined" size="small" fullWidth onClick={goHistory}>
            تاریخچه
          </Button>
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          فاکتورهای پرداخت‌نشده
        </Typography>
        {unpaidCount > 0 ? (
          <Chip size="small" color="error" label={unpaidCount.toLocaleString("fa-IR")} />
        ) : null}
      </Stack>

      {unpaidCount === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          فاکتور پرداخت‌نشده‌ای ندارید.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ mb: 1 }}>
          {unpaidInvoices.slice(0, 5).map((inv) => {
            const id = pick(inv, "invoiceId", "InvoiceId");
            const title =
              pick(inv, "title", "Title") ||
              pick(inv, "invoiceNumber", "InvoiceNumber") ||
              `فاکتور #${id}`;
            const amount = pick(inv, "remainingAmount", "RemainingAmount");
            const statusTitle = pick(inv, "statusTitle", "StatusTitle");
            const badgeState = mapOperationalStateToBadge("Payable");

            return (
              <ListItemButton
                key={id}
                onClick={() => goToInvoice(id)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  mb: 0.75,
                  py: 1,
                  px: 1.25,
                }}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {title}
                        </Typography>
                        <Typography variant="body2" color="primary.main" fontWeight={700}>
                          {formatRial(amount)}
                        </Typography>
                      </Box>
                      {statusTitle ? (
                        <PaymentStatusBadge state={badgeState} label={statusTitle} />
                      ) : null}
                      <ChevronLeftIcon fontSize="small" color="action" />
                    </Stack>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      )}

      <Button size="small" fullWidth variant="text" onClick={goAllInvoices}>
        مشاهده همه
      </Button>
    </Box>
  );

  return (
    <>
      <Tooltip title="فاکتورها و اعتبار پنل" arrow>
        <IconButton
          color="inherit"
          onClick={openMenu}
          aria-label="فاکتورها و اعتبار پنل"
          sx={{ mr: 0.5 }}
        >
          <Badge
            badgeContent={unpaidCount > 0 ? unpaidCount : null}
            color="error"
            overlap="circular"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 10,
                minWidth: 18,
                height: 18,
                fontWeight: 700,
              },
            }}
          >
            <ReceiptLongOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {!isMobile ? (
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 0.75,
              boxShadow: theme.shadows[8],
              overflow: "hidden",
            },
          }}
        >
          {panelContent}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={closeMenu}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: "85vh",
            },
          }}
        >
          {panelContent}
        </Drawer>
      )}
    </>
  );
};

HeaderFinanceMenu.propTypes = {};

export default HeaderFinanceMenu;
